<script lang="ts" setup>
import { computed, useRoute, nextTick, useCreators, useTemplateRef, watch, useAsyncData, useMaps, useExplore, ref, useRouter, useAuth, onMounted, type Component } from '#imports';
const { fetchSpots, spots, fetchMaps, maps, fetchDestinations, destinations, fetchDestinationCategories, destinationCategories } = useExplore();
import type { ComponentPublicInstance } from '#imports'
import type { Tables } from '~~/types/database.types'
import { watchDebounced, useMediaQuery } from '@vueuse/core';
const route = useRoute();
const router = useRouter();
const { fetchRecentMaps } = useMaps()
const { fetchFeaturedCreators } = useCreators();
await useAsyncData('destinations', () => fetchDestinations());

const { data: recentMaps } = await useAsyncData('recentMaps', () => fetchRecentMaps(4));
const { data: featuredCreators } = await useAsyncData('featuredCreators', () => fetchFeaturedCreators(4));

const spotsOrderedByDate = computed(() => {
  return [...spots.value].sort((a, b) => {
    const timeA = new Date(b.created_at).getTime();
    const timeB = new Date(a.created_at).getTime();
    return timeA - timeB;
  });
});

const menuItems = computed(() => [
  {
    label: 'Explore',
    id: 'explore',
    showLabel: false,
    withDivider: true,
    items: [
      {
        id: 'spots',
        label: 'Spots',
        icon: 'ri-map-pin-fill',
        isActive: route.path === '/explore' || route.path.startsWith('/explore/spots'),
        action: () => router.push('/explore')
      },
      {
        id: 'maps',
        label: 'Maps',
        icon: 'ri-map-2-fill',
        isActive: route.path.startsWith('/explore/maps'),
        action: () => router.push('/explore/maps')
      }
    ]
  },
]);

// Filters and Fetch
const categoryFilter = ref<string[]>([]);
const destinationFilter = ref<string[]>([]);
const spotFilters = computed(() => ({
  categories: {
    name: 'category',
    value: categoryFilter.value,
    referencedColumn: 'category_id',
  },
  destinations: {
    name: 'countries',
    value: destinationFilter.value,
    referencedTable: 'country_name',
    referencedColumn: 'country',
  },
}));

const mapFilters = computed(() => ({
  categories: {
    name: 'category',
    value: categoryFilter.value,
    referencedTable: 'spots',
    referencedColumn: 'category_id',
  },
  destinations: {
    name: 'countries',
    value: destinationFilter.value,
    referencedTable: 'spots.country_name',
    referencedColumn: 'country',
  },
}));

const openSpot = (spot: Tables<'spots'> & { map_slug: Tables<'maps'> }) => {
  if (route.path.startsWith('/explore/maps')) {
    router.push(`/maps/${spot?.map_slug?.current_slug}/spots/${spot.current_slug}`);
  } else {
    router.push(`/explore/${spot.current_slug}`);
  }
}

const featuredSpotsVisible = ref(true)

const minimizeResults = () => {
  console.log('Minimize')
  featuredSpotsVisible.value = !featuredSpotsVisible.value
}

watchDebounced(() => destinationFilter.value, async () => {
  categoryFilter.value = [];
  await fetchSpots(spotFilters.value);
  if (destinationFilter.value.length > 0) {
    await fetchDestinationCategories();
  }
}, { debounce: 500, maxWait: 1000, immediate: true});

watchDebounced(() => categoryFilter.value, async () => {
  await fetchSpots(spotFilters.value);
}, { debounce: 500, maxWait: 1000, immediate: true});

watchDebounced(() => destinationFilter.value, async () => {
  categoryFilter.value = [];
  await fetchMaps(mapFilters.value);
  if (destinationFilter.value.length > 0) {
    await fetchDestinationCategories();
  }
}, { debounce: 500, maxWait: 1000, immediate: true});

watchDebounced(() => categoryFilter.value, async () => {
  await fetchMaps(mapFilters.value);
}, { debounce: 500, maxWait: 1000, immediate: true});

const handleCategoryFilter = (categoryId: string) => {
  if (!categoryFilter.value.includes(categoryId)) {
    categoryFilter.value = [...categoryFilter.value, categoryId];
  } else {
    categoryFilter.value = categoryFilter.value.filter(id => id !== categoryId);
  }
};

const currentSpotSlug = computed(() => {
  return route.params.spotSlug
});

const childRouteActive = computed(() => {
  return route.path !== '/explore';
});

const isMapsPage = computed(() => {
  return route.path === '/explore/maps';
});

const isLessThanSm = useMediaQuery('(max-width: 920px)');

const mapComponent = ref<ComponentPublicInstance & {
  centerOnSpot: (spotId: string) => Promise<void>
} | null>(null);

const handleCenterSpot = async (spotId: string) => {
  await nextTick();
  if (mapComponent.value) {
    mapComponent.value.centerOnSpot(spotId);
  }
};

watch(() => route.params.spotSlug, async (spotSlug) => {
    await nextTick();
    if (spotSlug && mapComponent.value) {
      const spot = spots.value
        .find((s: Tables<'spots'>) => s.current_slug === spotSlug);
      if (spot) {
        await handleCenterSpot(spot.id);
      }
    }
  },
  { immediate: true },
);


const results = ref<HTMLElement & ComponentPublicInstance & { content: HTMLElement } | null>(null)
const spotRefs = ref<HTMLElement | null>(null)
const scrollToResult = async () => {
  await nextTick();
  if (!results.value || !results.value.content || !spotRefs.value) return;
  const activeElement = [...spotRefs.value].find(el => el.classList.contains('active'));
  if (!activeElement) return;
  const container = results.value.content;
  const ulPaddingLeft = 24;
  const desiredMargin = 20;
  const scrollPosition = activeElement.offsetLeft - ulPaddingLeft - (desiredMargin * 5);
  container.scrollTo({
    left: Math.max(0, scrollPosition), // Ensure we don't scroll past the beginning
    behavior: 'smooth'
  });
}

watch(() => currentSpotSlug.value, () => {
  scrollToResult()
}, { immediate: true });

watch(() => spotRefs.value, () => {
  scrollToResult()
}, { immediate: true });

const { setHeaderStyle } = useAuth();
onMounted(async () => {
  setHeaderStyle('opaque');
  await nextTick();
  scrollToResult()
});
</script>

<template>
  <div class="explore-map">
    <div id="header" :class="[{ 'active': destinationFilter.length > 0 }]">
      <div v-if="destinationFilter.length === 0">
        <h1 class="explore-title">
          Explore {{ route.path !== '/explore/maps' ? 'Spots' : 'Maps' }}
        </h1>
        <p class="explore-summary">
          Unlock insights from local experts & global creators, discover hidden gems and secret spots that go beyond the usual travel guides
        </p>
      </div>
      <ul>
        <li v-for="item in menuItems[0].items" :key="item.id">
          <a
            :label="item.label"
            :class="[{ active: item.isActive }]"
            @click="item.action"
          >
            <i :class="item.icon"/>
            {{ item.label }}
          </a>
        </li>
      </ul>
      <div :class="['select-destinations', { 'active': destinationFilter.length > 0 }]">
        <MPSelect
          v-if="destinations"
          hide-label
          label="Destinations"
          placeholder="Choose a Destination"
          v-model="destinationFilter"
          :options="destinations"
          searchable
          :multiple="destinationFilter.length > 0"
        />
        <MPButton v-if="destinationFilter.length === 0" version="primary" label="Explore" icon="ri-search-2-line" rounded icon-only hide-label class="search-button"/>
      </div>
    </div>

    <Transition name="fade-items">
      <div v-if="destinationFilter.length > 0" class="categoires-list">
        <TransitionGroup tag="ul" name="list" v-if="destinationCategories.length > 0">
          <li v-for="category in destinationCategories" :key="category.id" hydrate-on-visible>
            <a @click="handleCategoryFilter(category.id)" :class="['category-item', { 'active': categoryFilter.includes(category.id) }]">
              <i :class="category.icon"/>
              {{ category.name }}
            </a>
          </li>
        </TransitionGroup>
      </div>
    </Transition>
    <div id="map" :class="[{ 'active': destinationFilter.length > 0 }]">
      <ClientOnly>
        <MPMap
          ref="mapComponent"
          :locations="spots"
          :gesture-handling="isLessThanSm ? 'greedy' : 'cooperative'"
          fit-bounds
          @marker-click="openSpot($event)"
        />
      </ClientOnly>

      <Transition name="fade-items">
        <div v-if="!isLessThanSm || destinationFilter.length > 0" id="results">
          <div class="result-actions">
            <MPButton
              @click="minimizeResults"
              :label="featuredSpotsVisible ? 'Hide results' : 'Show results'"
              :icon="featuredSpotsVisible ? 'ri-arrow-down-double-line' : 'ri-arrow-up-double-line'"
              version="secondary"
              rounded
              size="small"
            />
          </div>
          <div v-if="!isMapsPage && featuredSpotsVisible" :class="['spots', { 'item-open': childRouteActive}]">
            <MPViewportSpill
              show-controls
              controls-position="top"
              ref="results"
            >
              <TransitionGroup tag="ul" name="list" v-if="spots">
                <li v-for="spot in spots" :key="spot.id" hydrate-on-visible ref="spotRefs" :class="{'active': spot.current_slug === currentSpotSlug}">
                  <div v-if="spot.current_slug === currentSpotSlug" class="spot-content">
                    <NuxtPage />
                  </div>
                  <MPSpotCard
                    v-if="spot.current_slug !== currentSpotSlug"
                    :spot="spot"
                    version="small"
                    showTier
                    actions
                    showDestination
                    :creator="spot.maps.profiles"
                    :to="`/explore/${spot.current_slug}`"
                    @click="handleCenterSpot(spot)"
                  />
                </li>
              </TransitionGroup>
            </MPViewportSpill>
          </div>
          <div v-if="isMapsPage && featuredSpotsVisible" class="maps">
            <MPViewportSpill
              show-controls
              controls-position="top"
            >
              <ul v-if="maps">
                <li v-for="map in maps" :key="map.id" hydrate-on-visible>
                  <MPMapCard
                    :map="map"
                    version="small"
                    :creator="map.profiles"
                    :hide-details="isLessThanSm"
                    :to="`/maps/${map.current_slug}`"
                  />
                </li>
              </ul>
            </MPViewportSpill>
          </div>
        </div>
      </Transition>
    </div>
    <div id="more">
      <hr class="pull-handle">
      <section hydrate-on-visible>
        <div class="more-header">
          <div>
            <h2>Featured Spots</h2>
            <p>Selected & curated for you by local experts & global creators.</p>
          </div>
          <div class="pull-up-graphic">
            <MPImg
              src="/images/pull-handle-graphic-0.png"
              :width="30"
              :height="30"
            />
            <MPImg
              src="/images/pull-handle-graphic-1.png"
              :width="30"
              :height="30"
            />
            <MPImg
              src="/images/pull-handle-graphic-2.png"
              :width="30"
              :height="30"
            />
          </div>
        </div>
        <MPViewportSpill>
          <LazyMPSpotCard
            class="spot-item"
            v-for="spot in spotsOrderedByDate.slice(0, 4)"
            :key="spot.id"
            :creator="spot.maps.profiles"
            actions
            :spot="spot"
            show-destination
            showTier
          />
        </MPViewportSpill>
      </section>
      <section hydrate-on-visible>
        <div class="more-header">
          <div>
            <h2>Recent Maps</h2>
            <p>View and discover the latest Maps Discover and support the people bringing the best of every place to you.</p>
          </div>
        </div>
        <MPViewportSpill>
          <LazyMPMapCard
            v-for="map in recentMaps"
            :key="map.id"
            :creator="map.profiles"
            :map="map"
            show-destination
            showTier
          />
        </MPViewportSpill>
      </section>
      <section hydrate-on-visible>
        <div class="more-header">
          <div>
            <h2>Featured Creators</h2>
            <p>Unlock insights from real creators, discover hidden gems and secret spots that go beyond the usual travel guides.</p>
          </div>
        </div>
        <MPViewportSpill>
          <LazyMPCreatorCard
            v-for="creator in featuredCreators"
            :key="creator.id"
            :creator="creator"
          />
        </MPViewportSpill>
      </section>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/breakpoints.pcss";
@import "@/assets/css/scrollbar.pcss";
@import "@/assets/css/typography.pcss";
@import "@/assets/css/layouts.pcss";

.explore-map {
  display: grid;
  min-height: 100dvh;
  width: 100vw;
  position: relative;
  top: -4.75rem;
  grid-template-columns: 1fr;
  @mixin screen md {
    top: 0;
  }
}

#header {
  z-index: 2;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(180deg, rgba(255, 255, 255, 1) 50%, rgba(255, 255, 255, 0.00) 100%);
  @mixin screen md {
    background: var(--color-slate-50);
    box-shadow: var(--shadow-sm);
    position: relative;
    top: 0;
    max-height: fit-content;
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    padding-bottom: 2.25rem;
    transition: padding 0.15s linear;
  }
}

#header.active {
  padding-bottom: 0.75rem;
  transition: padding 0.15s linear;
  /* transition-delay: 1s; */
}

#header .explore-title {
  display: none;
  @mixin screen md {
    display: block;
    margin: 0 auto;
    @mixin typeface-h3;
    max-width: 40rem;
    text-align: center;
    padding: 2rem 0 0.75rem;
  }
}

#header .explore-summary {
  display: none;
  @mixin screen md {
    display: block;
    margin: 0 auto;
    @mixin typeface-regular;
    max-width: 35rem;
    color: var(--color-slate-500);
    text-align: center;
    padding: 0 0 1rem;
  }
}

#header ul {
  list-style: none;
  padding: 0 0 0 3.75rem;
  margin: 0;
  display: inline-flex;
  overflow-x: scroll;
  @mixin scrollbar-reset;
  @mixin screen md {
    padding: 1rem 0 0;
  }
}

#header ul li a {
  display: block;
  padding: 1.15rem 0.65rem;
  text-decoration: none;
  color: var(--color-slate-500);
  transition: color 0.2s ease-in-out;
  @mixin screen md {
    padding: 0 1rem;
  }
}

#header ul li a i {
  font-size: 1.25rem;
}

#header ul li a.active {
  color: var(--color-slate-700);
  font-weight: 600;
  transition: color 0.2s ease-in-out;
}

#header .select-destinations {
  position: relative;
  display: flex;
  align-items: center;
  margin: 0 auto;
  margin-top: 0.5rem;
  width: 15rem;
  box-shadow: var(--shadow-sm);
  border-radius: 2rem;
  border: 1px solid var(--color-slate-100);
  transition: width 0.2s ease-in-out;

  @mixin screen md {
    width: 25rem;
    position: absolute;
    bottom: -2.5rem;
    transform: translateY(-50%);
  }

  :deep(label) {
    border-radius: 2rem;
    border: none;
    @mixin screen md {
      box-shadow: var(--shadow-sm);
    }
  }
  .search-button {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }
}

#header .select-destinations.active {
  width: calc(100vw - 2rem);
  transition: width 0.2s ease-in-out;
  @mixin screen md {
    width: 30rem;
    position: relative;
    transform: translateY(-100%);
    margin-top: 1rem;
    box-shadow: none;
  }
}

#map {
  position: fixed;
  top: 0;
  left: 0;
  height: 100dvh;
  width: 100vw;
  @mixin screen md {
    position: relative;
    top: 0;
    left: 0;
    height: 75dvh;
    width: 100%;
  }
}

/* #map.active {
  height: 75dvh;
  transition: height 0.1s ease-in;
  transition-delay: 0.2s;
} */

#results {
  z-index: 1;
  position: absolute;
  bottom: 9rem;
  left: 0;
  width: 100%;
  min-height: 2rem;
  @mixin screen md {
    bottom: 1rem;
  }
}

#results ul {
  list-style: none;
  margin: 0;
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  padding: 0 0 0 0.5rem;
  @mixin screen md {
    padding-left: 1.5rem;
  }
}

.result-actions {
  z-index: 5;
  pointer-events: auto;
  position: absolute;
  top: -0.25rem;
  left: 0.75rem;
  @mixin screen md {
    left: 1.5rem;
  }
}

#results .spots ul li {
  background-color: white;
  border-radius: 1rem;
  padding: 0.5rem;
  min-width: 22rem;
  height: 12rem;
  border: 1px solid var(--color-slate-200);
  box-shadow: var(--shadow-base);
  transition: all 0.2s ease-in-out;
  @mixin screen md {
    min-width: 24rem;
  }

  &:hover {
    border: 1px solid var(--color-indigo-200);
    box-shadow: var(--shadow-md);
    transition: all 0.2s ease-in-out;
  }
}

#results .maps ul li {
  background-color: white;
  border-radius: 1rem;
  padding: 0.5rem;
  width: 22rem;
  height: fit-content;
  border: 2px solid var(--color-slate-100);
  box-shadow: var(--shadow-md);

  @mixin screen md {
    width: 22rem;
  }

  &:deep(.map-card) {
    grid-template-columns: 9rem 1fr;
    @mixin screen md {
      grid-template-columns: 1fr;
    }
  }

  &:deep(.author) {
    white-space: nowrap;
    display: -webkit-box;
    line-clamp: 1;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:deep(.author a) {
    display: -webkit-box;
    line-clamp: 1;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:deep(.spots) {
    top: 0.75rem;
    left: 0.5rem;
    width: fit-content;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    @mixin screen md {
      padding: 0.15rem 0.5rem;
      align-items: center;
      top: 0.75rem;
      right: 0.5rem;
      left: auto;
    }
  }

  &:deep(.cover) {
    width: 100%;
    height: 10rem;
  }

  &:deep(.summary) {
    line-clamp: 1;
    -webkit-line-clamp: 1;
  }

  &:deep(.title a) {
    line-clamp: 1;
    -webkit-line-clamp: 1;
  }
}

#results {
  pointer-events: none;
}

#results ul {
  pointer-events: none;
}

#results ul li {
  pointer-events: auto;
}

$spotWidth: 30rem;
$spotHeight: 27.5rem;
#results .spots ul li.active {
  @mixin screen md {
    position: relative;
    min-width: $spotWidth;
    min-height: $spotHeight;
    border-radius: 0.5rem;
    overflow: hidden;
  }
}

.spot-content {
  @mixin screen md {
    position: absolute;
    top: 0;
    right: 0;
    width: $spotWidth;
    max-height: $spotHeight;
    transition: all 0.3s ease-in-out;
    overflow-y: scroll;
    @mixin scrollbar-reset;
  }
}

#more {
  z-index: 1;
  background-color: white;
  position: absolute;
  width: 100vw;
  min-height: 100dvh;
  height: fit-content;
  top: calc(100dvh - 8.5rem);
  left: 0;
  padding: 1rem;
  border-radius: 1rem 1rem 0 0;
  box-shadow: var(--shadow-md);
  @mixin screen md {
    position: relative;
    top: 0;
    left: 0;
    box-shadow: none;
    padding: 0;
    padding-top: 2rem;
  }
}

$gridGap: 1rem;
#more {
  padding-bottom: 2rem;
  &:deep(.swipe-content) {
    padding-left: 0;
    align-items: start;
    @mixin screen md {
      padding-left: 4.5vw;
    }
    @mixin screen lg {
      padding-left: 5vw;
    }
    @mixin screen xxl {
      padding-left: calc((100vw - 90rem) / 2);
    }
  }
  &:deep(.map-card),
  &:deep(.spot-card),
  &:deep(.creator-card) {
    min-width: 18rem;
    width: calc(90vw / 4 - $gridGap);
    max-width: 20rem;
    margin-right: $gridGap;
  }
}

#more section {
  padding-bottom: 2rem;
}

.more-header {
  @mixin layout-wrapper-wide;
  display: flex;
  align-items: top;
  justify-content: space-between;
}

.more-header h2 {
  @mixin typeface-h3;
  font-weight: 600;
  margin-bottom: 1rem;
}

.more-header p {
  @mixin typeface-regular;
  color: var(--color-slate-500);
  margin-bottom: 2rem;
}

#more .pull-handle {
  @mixin screen md {
    display: none;
  }
}

#more .pull-up-graphic {
  display: flex;
  @mixin screen md {
    display: none;
  }
  img {
    max-width: 60px;
    max-height: 60px;
    object-fit: cover;
    aspect-ratio: 1;
    position: relative;
    margin-left: -1.5rem;
  }
  img:nth-child(2) {
    z-index: 1;
  }
}

.categoires-list {
  z-index: 2;
  position: fixed;
  top: 8.5rem;
  @mixin screen md {
    position: absolute;
    top: 8rem;
    left: 0;
  }
  ul {
    max-width: 100vw;
    overflow-x: scroll;
    @mixin scrollbar-reset;
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    gap: 0.5rem;
    padding: 0 1rem 0.5rem 1rem;
  }
  .category-item {
    display: flex;
    gap: 0.25rem;
    background-color: white;
    white-space: nowrap;
    padding: 0.25rem 0.85rem;
    border-radius: 1rem;
    box-shadow: var(--shadow-md);
    border: 1px solid var(--color-slate-200);
  }
  i {
    color: var(--color-slate-500);
  }
  .category-item.active {
    background-color: var(--color-slate-500);
    color: var(--color-slate-50);
    transition: all 0.1s ease;
    border: 1px solid var(--color-slate-700);
  }
  .category-item.active i {
    color: var(--color-slate-100);
    transition: all 0.1s ease;
  }
}

.content {
  position: absolute;
  z-index: 2;
  left: 2vw;
  bottom: -1rem;
  max-width: 30rem;
  max-height: 65vh;
  overflow-y: scroll;
  z-index: 9000;
  @mixin scrollbar-reset;
  border-radius: 1rem 1rem 0 0;
}

.fade-items-enter-active {
  transition: opacity 0.2s ease;
  transition-delay: 1s;
}

.fade-items-enter-from,
.fade-items-leave-to {
  opacity: 0;
}

.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
